/**
 * Composes one locale's messages out of its per-feature modules (MAR-958).
 *
 * `i18n/locales/<locale>/index.ts` calls this with every module in that directory, keyed by the
 * module's file name. Each module owns whole top-level namespaces (`chat`, `documents`, …); this
 * merges them shallowly and THROWS if two modules define the same namespace. A shallow spread would
 * let the later module silently replace the earlier one's namespace, and every key only the earlier
 * one held would render as a raw key path — so a duplicate is refused at load time instead.
 *
 * Where a new key goes is in the Code organization conventions page (Notion Tech, §7).
 */
export type LocaleModule = Record<string, unknown>

/** `A | B` → `A & B`, so the composed locale keeps every module's literal message types. */
type UnionToIntersection<U> = (U extends unknown ? (x: U) => void : never) extends (x: infer I) => void ? I : never

export function composeLocale<M extends Record<string, LocaleModule>>(
  modules: M,
): UnionToIntersection<M[keyof M]> {
  const out: LocaleModule = {}
  const owner: Record<string, string> = {}
  for (const [moduleName, messages] of Object.entries(modules)) {
    for (const [namespace, value] of Object.entries(messages)) {
      const first = owner[namespace]
      if (first !== undefined) {
        throw new Error(
          `i18n namespace "${namespace}" is defined in two locale modules: ${first} and ${moduleName}`,
        )
      }
      owner[namespace] = moduleName
      out[namespace] = value
    }
  }
  return out as UnionToIntersection<M[keyof M]>
}
